import { useMemo } from 'react'
import { Input } from '@shared/components/ui/input'
import { Select, SelectContent, SelectTrigger, SelectValue, SelectCheckboxItem } from '@shared/components/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@shared/components/ui/tooltip'
import { cn } from '@shared/lib/utils'
import { UNITS } from '@features/products/components/sheets/steps/constants'
import { SELECTS, INPUTS } from './styles'

interface UnitSelectFieldProps {
  value: string
  showCustomInput: boolean
  customValue: string
  onUnitChange: (unit: string) => void
  onCustomValueChange: (value: string) => void
  onOpenChange: (open: boolean) => void
  className?: string
}


export function UnitSelectField({
  value,
  showCustomInput,
  customValue,
  onUnitChange,
  onCustomValueChange,
  onOpenChange,
  className
}: UnitSelectFieldProps) {
  // Memoize options to prevent recreation on every render
  const unitOptions = useMemo(() => [
    ...UNITS,
    { value: 'other', label: 'Other' }
  ], [])

  // Extract display value calculation for clarity
  const displayValue = showCustomInput ? (customValue || 'units') : (value || 'units')

  // Extract custom input rendering for better readability
  const renderCustomInput = () => (
    <div className="p-4">
      <Input
        placeholder="Unit type"
        value={customValue}
        onChange={(e) => onCustomValueChange(e.target.value)}
        className={INPUTS.customUnit}
        onClick={(e) => e.stopPropagation()}
        maxLength={10}
        autoFocus
      />
    </div>
  )
  return (
    <TooltipProvider delayDuration={300}>
      <Select 
        value={value} 
        onValueChange={onUnitChange}
        onOpenChange={onOpenChange}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <SelectTrigger className={cn(SELECTS.unit, className)}>
              <SelectValue>
                {displayValue}
              </SelectValue>
            </SelectTrigger>
          </TooltipTrigger>
          <TooltipContent 
            side="top" 
            className="max-w-48"
            align="center"
            avoidCollisions={true}
            collisionPadding={10}
          >
            Choose the unit your pricing is based on. This must match something your product tracks.
          </TooltipContent>
        </Tooltip>
      <SelectContent 
        className={SELECTS.unitContent}
        side="bottom"
        align="start"
        avoidCollisions={true}
        collisionPadding={20}
        sideOffset={4}
      >
        {unitOptions.map((unitOption) => {
          // Simplified selection logic while maintaining behavior
          const isSelected = unitOption.value === 'other' ? showCustomInput : !showCustomInput && value === unitOption.value
          
          return (
            <div key={unitOption.value}>
              <SelectCheckboxItem
                checked={isSelected}
                onCheckedChange={(checked) => checked && onUnitChange(unitOption.value)}
                className={cn(
                  isSelected && "font-medium",
                  unitOption.value === 'other' && showCustomInput && "border-b-0"
                )}
              >
                {unitOption.label}
              </SelectCheckboxItem>
              {unitOption.value === 'other' && showCustomInput && renderCustomInput()}
            </div>
          )
        })}
      </SelectContent>
    </Select>
    </TooltipProvider>
  )
}